import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Form, Date, Select3 } from "@weezevent/nacre";
import moment from "moment";
import {
	SELECT_FIELD_TYPE,
	CHECKBOX_FIELD_TYPE,
	BIRTHDATE_FIELD_TYPE,
	CONDITION_ISCHECKED,
	CONDITION_ISEMPTY,
	CONDITION_RANGE,
	DATE_CONDITIONS
} from "../../../../models/forms-with-entry-usage";

const ValueInput = ({
	fieldType,
	selectedCondition,
	currentValue,
	onValueChange,
	onDateChange,
	fieldChoices = [],
	t
}) => {
	const { i18n } = useTranslation();
	const locale = i18n.accountLocale;

	const choiceOptions = React.useMemo(() => {
		if (fieldType !== SELECT_FIELD_TYPE || !fieldChoices) return [];

		const choicesArray = Array.isArray(fieldChoices)
			? fieldChoices
			: Object.values(fieldChoices);

		return choicesArray.map(choice => {
			const val = Array.isArray(choice)
				? choice[0]
				: choice?.value ?? choice;
			const label = Array.isArray(choice)
				? choice[1] || choice[0]
				: choice?.text || choice?.label || val;
			return { value: String(val), text: String(label) };
		});
	}, [fieldChoices, fieldType]);

	const selectedValues = React.useMemo(() => {
		if (fieldType !== SELECT_FIELD_TYPE) return [];
		if (Array.isArray(currentValue)) return currentValue.map(String);
		return currentValue ? [String(currentValue)] : [];
	}, [currentValue, fieldType]);

	if (fieldType === SELECT_FIELD_TYPE) {
		return (
			<Form.Field>
				<Form.Label>
					{t(
						"access.attendees.attendeesList.filters.formFields.value"
					)}
				</Form.Label>
				<Select3
					options={choiceOptions}
					value={selectedValues}
					onChange={vals =>
						onValueChange(Array.isArray(vals) ? vals : [vals])
					}
					multiple
					search
					placeholder={t(
						"access.attendees.attendeesList.filters.formFields.selectValue"
					)}
				/>
			</Form.Field>
		);
	}

	if (!selectedCondition || selectedCondition === CONDITION_ISEMPTY) {
		return null;
	}

	if (
		fieldType === CHECKBOX_FIELD_TYPE &&
		selectedCondition === CONDITION_ISCHECKED
	) {
		const isChecked = currentValue === true || currentValue === "true";

		return (
			<Form.Field>
				<Form.Label>
					{t(
						"access.attendees.attendeesList.filters.formFields.conditionAndValue"
					)}
				</Form.Label>
				<div>
					<Form.RadioLabel
						bordered
						inline
						checked={isChecked}
						onChange={() => onValueChange(true)}
					>
						{t(
							"access.attendees.attendeesList.filters.formFields.checked"
						)}
					</Form.RadioLabel>
					<Form.RadioLabel
						bordered
						inline
						checked={!isChecked}
						onChange={() => onValueChange(false)}
					>
						{t(
							"access.attendees.attendeesList.filters.formFields.notchecked"
						)}
					</Form.RadioLabel>
				</div>
			</Form.Field>
		);
	}

	if (fieldType === BIRTHDATE_FIELD_TYPE) {
		const renderDateInput = (val, key, labelKey) => (
			<Form.Field>
				<Form.Label>
					{t(
						`access.attendees.attendeesList.filters.formFields.${labelKey}`
					)}
				</Form.Label>
				<Date.Input
					locale={locale}
					value={val ? moment(val).toISOString() : null}
					onChange={date => onDateChange(date, key)}
				/>
			</Form.Field>
		);

		if (selectedCondition === CONDITION_RANGE) {
			return (
				<>
					{renderDateInput(
						currentValue?.value_start,
						"value_start",
						"startDate"
					)}
					{renderDateInput(
						currentValue?.value_end,
						"value_end",
						"endDate"
					)}
				</>
			);
		}

		if (DATE_CONDITIONS.includes(selectedCondition)) {
			return renderDateInput(
				currentValue?.value_start,
				"value_start",
				"date"
			);
		}
	}

	return (
		<Form.Field>
			<Form.Label>
				{t("access.attendees.attendeesList.filters.formFields.value")}
			</Form.Label>
			<Form.Input
				value={currentValue || ""}
				onChange={e => onValueChange(e.target.value)}
			/>
		</Form.Field>
	);
};

ValueInput.propTypes = {
	fieldType: PropTypes.string,
	selectedCondition: PropTypes.string,
	fieldChoices: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
	currentValue: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.object,
		PropTypes.array,
		PropTypes.bool
	]),
	onValueChange: PropTypes.func.isRequired,
	onDateChange: PropTypes.func.isRequired,
	t: PropTypes.func.isRequired
};

export default ValueInput;
